import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Button, Col, Drawer, Row, Table } from 'antd';
import type { TableProps } from 'antd';
import { DeleteOutlined, EditOutlined, ImportOutlined } from '@ant-design/icons';
import { ServicesConfigData, TranslationData } from '../../../../../../models/MappingData';
import { deleteServiceConfigMapping, fetchServicesConfigMapping } from '../../../../../../api/repositoryEP';
import ServicesForm from '../Forms/ServicesForm/ServicesForm';
import Search from 'antd/es/input/Search';
import { PageSize } from '../../../../../../utils/constants';
import { CustomPagination } from '../../../../..';
import { MaterialSymbol } from 'react-material-symbols';
import type { MaterialSymbolProps } from 'react-material-symbols';
import "react-material-symbols/outlined"; // <-- IMPORTANTE
import { GenericModal } from '../../../../../common/Modal/GenericModal';

interface ServicesConfigProps {
    language: string;
}

const ServicesConfig = (props: ServicesConfigProps): JSX.Element => {

    const { t } = useTranslation();
    const [data, setData] = useState<ServicesConfigData[]>([]);
    const [serviceData, setServiceData] = useState<ServicesConfigData | null>(null);
    const drawerWidth = window.innerWidth <= 768 ? '100%' : '40%';
    const [newServiceDrawer, setNewServiceDrawer] = useState<boolean>(false);
    const [editServiceDrawer, setEditServiceDrawer] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [actualPage, setActualPage] = useState<number>(1);
    const [actualPageSize, setActualPageSize] = useState<number>(PageSize);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [filter, setFilter] = useState<string>('');


    const onChangePagination = (newPage: number, newPageSize?: number | undefined) => {
        setActualPage(newPage);
        setActualPageSize(newPageSize ?? PageSize);
    }

    const openDeleteModal = (service: ServicesConfigData) => {
        setServiceData(service);
        setDeleteModalVisible(true);
    }
    useEffect(() => { }, [serviceData]);

    const i18n = useTranslation().i18n;

    useEffect(() => {
        if (!newServiceDrawer && !editServiceDrawer && !deleteModalVisible) {
            setLoading(true);
            fetchServicesConfigMapping(filter, i18n.language, actualPage, actualPageSize).then((response) => {
                if (response?.type === 1) {
                    setData(response.data.elements);
                    setTotalRecords(response.data.totalRecords as number);
                }
                setLoading(false);
            });
        }
    }, [newServiceDrawer, editServiceDrawer, deleteModalVisible, i18n.language, filter, actualPage, actualPageSize]);

    const columns: TableProps<ServicesConfigData>['columns'] = [
        {
            title: t("components.tab-mapping.services-config.table.c1"),
            dataIndex: 'name',
            key: 'name',
            render: (names: TranslationData[] = [], record: ServicesConfigData) => {
                const normalizedLanguage = i18n.language?.slice(0, 2).toLowerCase() ?? "";

                const nameInLanguage = names.find(
                    (name: TranslationData) => (name.language?.slice(0, 2).toLowerCase() ?? "") === normalizedLanguage
                );

                return (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {nameInLanguage?.value || "No name available"}
                        {record.icon && (

                            <MaterialSymbol
                                style={{ fontFamily: "Material Symbols Outlined" }}
                                icon={record.icon as MaterialSymbolProps["icon"]}
                                size={20} />
                        )}
                    </div>
                );
            }
        }, 
        {
            title: t("components.tab-mapping.services-config.table.c2"),
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: t("components.tab-mapping.services-config.table.c4"),
            dataIndex: 'internCode',
            key: 'internCode',
        },
        {
            title: t("components.tab-mapping.services-config.table.c5"),
            key: 'action',
            render: (obj) => (
                <div className='app-icon-table-container' >
                    <EditOutlined className='app-icon-table' onClick={() => editService(obj)} />
                    <DeleteOutlined className='app-icon-table' onClick={() => openDeleteModal(obj)} />
                </div>
            ),
        }
    ]


    const editService = (obj: ServicesConfigData) => {
        setServiceData(obj);
        setEditServiceDrawer(true);
    }

    const onSearch = (value: string) => {
        setFilter(value);
        setActualPage(1); //  Siempre empezamos en la primera página al buscar
    };
    const [deletingService, setDeletingService] = useState<boolean>(false);

    const cancelDeleteService = () => {

        setDeleteModalVisible(false);
        setServiceData(null);
    }

    const handleAddButton = () => {
        setServiceData(null);
        setNewServiceDrawer(true);
    }
    const deleteService = async () => {
        if (serviceData?.id) {
            setDeletingService(true);
            deleteServiceConfigMapping(serviceData.id).then((r) => {
                if (r?.type === 1) {
                    setDeletingService(false);
                    setDeleteModalVisible(false);
                    setServiceData(null);
                } else {
                    setDeletingService(false);
                }
            });
        }
    };


    return (
        <div className="services-configuration-component">
            {serviceData && (
                <GenericModal
                    isLoading={deletingService}
                    title={t("components.tab-mapping.services-config.modal-title")}
                    closable
                    content={<div>{t("components.tab-mapping.services-config.modal-msg")}</div>}
                    visible={deleteModalVisible}
                    onOk={deleteService}
                    onCancel={cancelDeleteService}
                />
            )}


            <Drawer
                open={editServiceDrawer}
                title={t("components.tab-mapping.services-config.drawer.title-edit")}
                onClose={() => {
                    setServiceData(null)
                    setEditServiceDrawer(false);
                }}
                width={drawerWidth}
                closable
            >

                <ServicesForm
                    setDrawerVisible={setEditServiceDrawer}
                    drawerVisible={editServiceDrawer}
                    serviceData={serviceData}
                    setServiceData={setServiceData}
                />
            </Drawer>


            <Drawer
                open={newServiceDrawer}
                title={t("components.tab-mapping.services-config.drawer.title-new")}
                onClose={() => {
                    setServiceData(null)
                    setNewServiceDrawer(false);
                }}
                width={drawerWidth}
                closable
            >

                <ServicesForm
                    setDrawerVisible={setNewServiceDrawer}
                    drawerVisible={newServiceDrawer}
                    serviceData={serviceData}
                    setServiceData={setServiceData}
                />
            </Drawer>

            <Row gutter={[10, 10]} style={{ flexDirection: 'column' }}>
                <Col xs={24} md={24}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={24} lg={16}>

                        </Col>
                        <Col xs={24} md={24} style={{ fontFamily: 'Montserrat' }}>
                            {t("components.tab-mapping.services-config.msg-info")}
                        </Col>
                        <Col xs={24} md={24} lg={16}>
                            <Search
                                placeholder={t("components.tab-mapping.services-config.search") || ''}
                                allowClear
                                onSearch={onSearch}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={4}>
                            <Button
                                className='app-button'
                                onClick={() => handleAddButton()}>
                                {t("components.tab-mapping.room-config.button-add")}
                            </Button>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={4}>
                            <Button disabled className='app-button'><ImportOutlined /> {t("components.tab-mapping.room-config.button-import")}</Button>
                        </Col>
                    </Row>
                </Col>
                <Col xs={24} md={24}>
                    <Table
                        className='table'
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        scroll={{ x: 'max-content' }}
                        pagination={false}

                    />
                </Col>
            </Row>
            {(
                totalRecords > 0 && (
                    <div style={{ marginTop: '15px' }}>
                        <CustomPagination
                            actualPage={actualPage}
                            actualPageSize={actualPageSize}
                            totalElements={totalRecords}
                            onChange={onChangePagination}
                        />
                    </div>
                )
            )}

        </div>
    );
};



export default ServicesConfig;